import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  CROP_HANDLES,
  STAGE_PADDING_PX,
  ZOOM_STEP,
  computeContainLayout,
  cropBoundsForImage,
  ensureImageCoversCrop,
  isFullImageCrop,
  maxScaleForLayout,
  minCropSize,
  minScaleToCover,
  moveCropRect,
  orientedSize,
  pixelCropFromLayout,
  relayoutPreservingView,
  applyScaleAround,
  resizeCropRect
} from '../../helpers/imageCropGeometry'
import { exportOrientedCropToJpeg, sourceToBlob } from '../../helpers/imageCropExport'
import './ImageCrop.css'

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const DOUBLE_TAP_MS = 280
const DOUBLE_TAP_PX = 28
const NUDGE_PX = 1
const NUDGE_FAST_PX = 10

function pointFromEvent(event, stageEl) {
  const rect = stageEl.getBoundingClientRect()
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  }
}

function pointerDistance(a, b) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}

function pointerMidpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

function RotateLeftIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 4L5 8l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 8h8a6 6 0 1 1 0 12h-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RotateRightIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 8h-8a6 6 0 1 0 0 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ResetIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12a8 8 0 1 0 2.3-5.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 4v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MinusIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 6v12M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

const ImageCrop = ({ imageSrc, onCropComplete, onCancel }) => {
  const dialogRef = useRef(null)
  const stageRef = useRef(null)
  const imageRef = useRef(null)
  const layoutRef = useRef(null)
  const pointersRef = useRef(new Map())
  const gestureRef = useRef(null)
  const frameRef = useRef(null)
  const pendingLayoutRef = useRef(null)
  const lastTapRef = useRef(null)
  const naturalRef = useRef({ width: 0, height: 0 })
  const rotationRef = useRef(0)
  const isMountedRef = useRef(true)
  const previousFocusRef = useRef(null)
  const loadIdRef = useRef(0)

  const [status, setStatus] = useState('loading')
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 })
  const [rotation, setRotation] = useState(0)
  const [layout, setLayout] = useState(null)
  const [busy, setBusy] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  const commitLayout = useCallback((next) => {
    layoutRef.current = next
    pendingLayoutRef.current = next
    setLayout(next)
  }, [])

  const scheduleLayout = useCallback((next) => {
    layoutRef.current = next
    pendingLayoutRef.current = next
    if (frameRef.current != null) return
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null
      if (!isMountedRef.current) return
      setLayout(pendingLayoutRef.current)
    })
  }, [])

  const measureStage = useCallback(() => {
    const stage = stageRef.current
    if (!stage) return { width: 0, height: 0 }
    return { width: stage.clientWidth, height: stage.clientHeight }
  }, [])

  const oriented = orientedSize(naturalSize.width, naturalSize.height, rotation)

  const fitToStage = useCallback((rot = rotationRef.current, nat = naturalRef.current) => {
    const stage = measureStage()
    if (stage.width < 1 || stage.height < 1 || nat.width < 1 || nat.height < 1) return null
    const size = orientedSize(nat.width, nat.height, rot)
    return computeContainLayout(stage.width, stage.height, size.width, size.height, STAGE_PADDING_PX)
  }, [measureStage])

  const handleImageReady = useCallback((img) => {
    if (!img || !img.naturalWidth || !img.naturalHeight) return
    const loadId = loadIdRef.current
    const nat = { width: img.naturalWidth, height: img.naturalHeight }
    naturalRef.current = nat
    setNaturalSize(nat)
    setErrorMessage('')
    const next = fitToStage(rotationRef.current, nat)
    if (!isMountedRef.current || loadId !== loadIdRef.current) return
    if (next) {
      commitLayout(next)
      setStatus('ready')
    }
  }, [commitLayout, fitToStage])

  useEffect(() => {
    isMountedRef.current = true
    previousFocusRef.current = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const id = window.requestAnimationFrame(() => {
      if (dialogRef.current) dialogRef.current.focus()
    })
    return () => {
      isMountedRef.current = false
      document.body.style.overflow = previousOverflow
      window.cancelAnimationFrame(id)
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current)
      const prev = previousFocusRef.current
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
  }, [])

  const imageSrcRef = useRef(imageSrc)
  useEffect(() => {
    if (imageSrcRef.current === imageSrc) return
    imageSrcRef.current = imageSrc
    loadIdRef.current += 1
    setStatus('loading')
    setLayout(null)
    layoutRef.current = null
    setErrorMessage('')
    setBusy(null)
    rotationRef.current = 0
    setRotation(0)
    naturalRef.current = { width: 0, height: 0 }
    setNaturalSize({ width: 0, height: 0 })
    pointersRef.current.clear()
    gestureRef.current = null
  }, [imageSrc])

  useEffect(() => {
    const img = imageRef.current
    if (img && img.complete && img.naturalWidth > 0) {
      handleImageReady(img)
    }
  }, [imageSrc, handleImageReady])

  useLayoutEffect(() => {
    const stage = stageRef.current
    if (!stage || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(() => {
      const nat = naturalRef.current
      if (nat.width < 1) return
      const stageSize = measureStage()
      const size = orientedSize(nat.width, nat.height, rotationRef.current)
      const prev = layoutRef.current
      const next = relayoutPreservingView(
        prev,
        stageSize.width,
        stageSize.height,
        size.width,
        size.height,
        STAGE_PADDING_PX
      )
      commitLayout(next)
      setStatus('ready')
    })
    observer.observe(stage)
    return () => observer.disconnect()
  }, [commitLayout, measureStage, status])

  const zoomBy = useCallback((factor, focal) => {
    const current = layoutRef.current
    const nat = naturalRef.current
    if (!current || nat.width < 1) return
    const size = orientedSize(nat.width, nat.height, rotationRef.current)
    const minScale = minScaleToCover(current.crop, size.width, size.height)
    const maxScale = maxScaleForLayout(current.containScale)
    const nextScale = Math.min(maxScale, Math.max(minScale, current.image.scale * factor))
    const focus = focal || {
      x: current.crop.x + current.crop.width / 2,
      y: current.crop.y + current.crop.height / 2
    }
    let image = applyScaleAround(current.image, nextScale, focus.x, focus.y, size.width, size.height)
    image = ensureImageCoversCrop(image, current.crop, size.width, size.height, maxScale)
    commitLayout({ ...current, image })
  }, [commitLayout])

  const applyRotation = useCallback((nextRotation) => {
    rotationRef.current = nextRotation
    setRotation(nextRotation)
    const next = fitToStage(nextRotation, naturalRef.current)
    if (next) commitLayout(next)
  }, [commitLayout, fitToStage])

  const handleRotate = useCallback((delta) => {
    if (busy) return
    applyRotation((rotationRef.current + delta + 360) % 360)
  }, [applyRotation, busy])

  const handleReset = useCallback(() => {
    if (busy) return
    const next = fitToStage(rotationRef.current, naturalRef.current)
    if (next) commitLayout(next)
  }, [busy, commitLayout, fitToStage])

  const onPointerDown = useCallback((event) => {
    if (busy || status !== 'ready' || !layoutRef.current) return
    const stage = stageRef.current
    if (!stage) return

    if (event.pointerType === 'mouse' && event.button !== 0) return

    event.preventDefault()
    if (typeof stage.setPointerCapture === 'function') {
      try {
        stage.setPointerCapture(event.pointerId)
      } catch (error) {
        // Some browsers throw if the pointer id is not an active OS pointer.
      }
    }
    const point = pointFromEvent(event, stage)
    pointersRef.current.set(event.pointerId, point)

    const handle = event.target && event.target.getAttribute
      ? event.target.getAttribute('data-crop-handle')
      : null

    if (pointersRef.current.size >= 2) {
      const pts = Array.from(pointersRef.current.values())
      gestureRef.current = {
        type: 'pinch',
        startDistance: pointerDistance(pts[0], pts[1]),
        startScale: layoutRef.current.image.scale,
        startMid: pointerMidpoint(pts[0], pts[1]),
        originImage: layoutRef.current.image,
        originLayout: layoutRef.current
      }
      lastTapRef.current = null
      return
    }

    if (handle) {
      gestureRef.current = {
        type: 'resize',
        handle,
        origin: point,
        originCrop: layoutRef.current.crop,
        originLayout: layoutRef.current
      }
      lastTapRef.current = null
      return
    }

    const now = Date.now()
    const lastTap = lastTapRef.current
    if (
      lastTap &&
      now - lastTap.time < DOUBLE_TAP_MS &&
      pointerDistance(point, lastTap.point) < DOUBLE_TAP_PX
    ) {
      lastTapRef.current = null
      const current = layoutRef.current
      const atMin = current.image.scale <= current.containScale * 1.02
      zoomBy(atMin ? 2 : 1 / (current.image.scale / current.containScale), point)
      pointersRef.current.delete(event.pointerId)
      gestureRef.current = null
      return
    }
    lastTapRef.current = { time: now, point }

    gestureRef.current = {
      type: 'pan',
      origin: point,
      originImage: layoutRef.current.image,
      originLayout: layoutRef.current
    }
  }, [busy, status, zoomBy])

  const onPointerMove = useCallback((event) => {
    if (!pointersRef.current.has(event.pointerId)) return
    const stage = stageRef.current
    const current = layoutRef.current
    const nat = naturalRef.current
    if (!stage || !current || nat.width < 1) return

    const point = pointFromEvent(event, stage)
    pointersRef.current.set(event.pointerId, point)
    const size = orientedSize(nat.width, nat.height, rotationRef.current)
    const maxScale = maxScaleForLayout(current.containScale)
    const gesture = gestureRef.current

    if (pointersRef.current.size >= 2) {
      const pts = Array.from(pointersRef.current.values())
      const distance = pointerDistance(pts[0], pts[1])
      let pinch = gesture && gesture.type === 'pinch' ? gesture : null
      if (!pinch || !pinch.startDistance || !pinch.originImage || !pinch.startMid) {
        pinch = {
          type: 'pinch',
          startDistance: distance,
          startScale: current.image.scale,
          startMid: pointerMidpoint(pts[0], pts[1]),
          originImage: current.image,
          originLayout: current
        }
        gestureRef.current = pinch
      }
      if (pinch.startDistance < 1) return
      const mid = pointerMidpoint(pts[0], pts[1])
      const minScale = minScaleToCover(current.crop, size.width, size.height)
      const nextScale = Math.min(
        maxScale,
        Math.max(minScale, pinch.startScale * (distance / pinch.startDistance))
      )
      let image = applyScaleAround(
        pinch.originImage,
        nextScale,
        pinch.startMid.x,
        pinch.startMid.y,
        size.width,
        size.height
      )
      image = {
        ...image,
        x: image.x + (mid.x - pinch.startMid.x),
        y: image.y + (mid.y - pinch.startMid.y)
      }
      image = ensureImageCoversCrop(image, current.crop, size.width, size.height, maxScale)
      scheduleLayout({ ...current, image })
      return
    }

    if (!gesture) return

    if (gesture.type === 'pan') {
      const dx = point.x - gesture.origin.x
      const dy = point.y - gesture.origin.y
      const image = ensureImageCoversCrop(
        {
          x: gesture.originImage.x + dx,
          y: gesture.originImage.y + dy,
          scale: gesture.originImage.scale
        },
        current.crop,
        size.width,
        size.height,
        maxScale
      )
      scheduleLayout({ ...current, image })
      return
    }

    if (gesture.type === 'resize') {
      const dx = point.x - gesture.origin.x
      const dy = point.y - gesture.origin.y
      const minSize = minCropSize(gesture.originLayout.image, size.width, size.height)
      const bounds = cropBoundsForImage(
        gesture.originLayout.image,
        size.width,
        size.height,
        current.stageWidth,
        current.stageHeight,
        STAGE_PADDING_PX
      )
      const crop = resizeCropRect(gesture.originCrop, gesture.handle, dx, dy, minSize, bounds)
      const image = ensureImageCoversCrop(
        gesture.originLayout.image,
        crop,
        size.width,
        size.height,
        maxScale
      )
      scheduleLayout({ ...current, crop, image })
    }
  }, [scheduleLayout])

  const onPointerUp = useCallback((event) => {
    pointersRef.current.delete(event.pointerId)
    if (pointersRef.current.size === 0) {
      gestureRef.current = null
    } else if (pointersRef.current.size === 1 && layoutRef.current) {
      const remaining = Array.from(pointersRef.current.values())[0]
      gestureRef.current = {
        type: 'pan',
        origin: remaining,
        originImage: layoutRef.current.image,
        originLayout: layoutRef.current
      }
    }
  }, [])

  const onWheel = useCallback((event) => {
    if (busy || status !== 'ready' || !layoutRef.current) return
    event.preventDefault()
    const stage = stageRef.current
    if (!stage) return
    const point = pointFromEvent(event, stage)
    const direction = event.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP
    zoomBy(direction, point)
  }, [busy, status, zoomBy])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return undefined
    const handleWheel = (event) => onWheel(event)
    stage.addEventListener('wheel', handleWheel, { passive: false })
    return () => stage.removeEventListener('wheel', handleWheel)
  }, [onWheel])

  const pixelCrop = layout && naturalSize.width
    ? pixelCropFromLayout({
      crop: layout.crop,
      imageX: layout.image.x,
      imageY: layout.image.y,
      scale: layout.image.scale,
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
      rotation
    })
    : null

  const exportFullOrOriginal = useCallback(async () => {
    const img = imageRef.current
    if (!img) throw new Error('Image is not ready')
    if (rotationRef.current === 0) {
      const original = await sourceToBlob(imageSrc)
      if (/jpe?g/i.test(original.type || '')) {
        return original
      }
    }
    const size = orientedSize(naturalRef.current.width, naturalRef.current.height, rotationRef.current)
    return exportOrientedCropToJpeg(
      img,
      { x: 0, y: 0, width: size.width, height: size.height },
      rotationRef.current
    )
  }, [imageSrc])

  const handleSkip = useCallback(async () => {
    if (busy || status !== 'ready') return
    setBusy('skipping')
    setErrorMessage('')
    try {
      const blob = await exportFullOrOriginal()
      if (!isMountedRef.current) return
      onCropComplete(blob)
    } catch (error) {
      if (!isMountedRef.current) return
      setErrorMessage(error.message || 'Could not use the full image. Try again.')
      setBusy(null)
    }
  }, [busy, exportFullOrOriginal, onCropComplete, status])

  const handleConfirm = useCallback(async () => {
    if (busy || status !== 'ready' || !layoutRef.current) return
    const current = layoutRef.current
    const nat = naturalRef.current
    const cropPixels = pixelCropFromLayout({
      crop: current.crop,
      imageX: current.image.x,
      imageY: current.image.y,
      scale: current.image.scale,
      naturalWidth: nat.width,
      naturalHeight: nat.height,
      rotation: rotationRef.current
    })
    if (!cropPixels.width || !cropPixels.height) {
      setErrorMessage('Crop area is too small')
      return
    }
    setBusy('cropping')
    setErrorMessage('')
    try {
      const img = imageRef.current
      if (!img) throw new Error('Image is not ready')
      const size = orientedSize(nat.width, nat.height, rotationRef.current)
      const fullImage = isFullImageCrop(cropPixels, size.width, size.height)
      const blob = fullImage
        ? await exportFullOrOriginal()
        : await exportOrientedCropToJpeg(img, cropPixels, rotationRef.current)
      if (!isMountedRef.current) return
      onCropComplete(blob)
    } catch (error) {
      if (!isMountedRef.current) return
      setErrorMessage(error.message || 'Could not crop that image. Try again.')
      setBusy(null)
    }
  }, [busy, exportFullOrOriginal, onCropComplete, status])

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      if (!busy) onCancel()
      return
    }

    if (event.key === 'Enter' && !event.repeat && event.target === dialogRef.current) {
      event.preventDefault()
      handleConfirm()
      return
    }

    if (event.key === 'Tab' && dialogRef.current) {
      const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((node) => node.offsetParent !== null)
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
      return
    }

    if (busy || status !== 'ready' || !layoutRef.current) return
    const current = layoutRef.current
    const nat = naturalRef.current
    const size = orientedSize(nat.width, nat.height, rotationRef.current)
    const maxScale = maxScaleForLayout(current.containScale)
    const minSize = minCropSize(current.image, size.width, size.height)
    const bounds = cropBoundsForImage(
      current.image,
      size.width,
      size.height,
      current.stageWidth,
      current.stageHeight,
      STAGE_PADDING_PX
    )

    if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      zoomBy(ZOOM_STEP)
      return
    }
    if (event.key === '-' || event.key === '_') {
      event.preventDefault()
      zoomBy(1 / ZOOM_STEP)
      return
    }
    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault()
      handleRotate(event.shiftKey ? -90 : 90)
      return
    }

    const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }
    const dir = arrows[event.key]
    if (!dir) return
    event.preventDefault()
    const step = event.shiftKey ? NUDGE_FAST_PX : NUDGE_PX

    if (event.altKey) {
      const image = ensureImageCoversCrop(
        {
          x: current.image.x - dir[0] * step,
          y: current.image.y - dir[1] * step,
          scale: current.image.scale
        },
        current.crop,
        size.width,
        size.height,
        maxScale
      )
      commitLayout({ ...current, image })
      return
    }

    const crop = moveCropRect(current.crop, dir[0] * step, dir[1] * step, bounds, minSize)
    const image = ensureImageCoversCrop(current.image, crop, size.width, size.height, maxScale)
    commitLayout({ ...current, crop, image })
  }, [busy, commitLayout, handleConfirm, handleRotate, onCancel, status, zoomBy])

  const disabled = Boolean(busy) || status !== 'ready'
  const imageStyle = layout
    ? (() => {
      const scale = layout.image.scale
      const dispNatW = naturalSize.width * scale
      const dispNatH = naturalSize.height * scale
      const dispOrientedW = oriented.width * scale
      const dispOrientedH = oriented.height * scale
      const cx = layout.image.x + dispOrientedW / 2
      const cy = layout.image.y + dispOrientedH / 2
      return {
        width: `${dispNatW}px`,
        height: `${dispNatH}px`,
        left: `${cx - dispNatW / 2}px`,
        top: `${cy - dispNatH / 2}px`,
        transform: `rotate(${rotation}deg)`
      }
    })()
    : undefined

  return (
    <div className="image-crop-modal">
      <div
        ref={dialogRef}
        className="image-crop-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="image-crop-title"
        aria-describedby="image-crop-hint"
        aria-busy={busy ? 'true' : 'false'}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="image-crop-header">
          <div className="image-crop-header-text">
            <h3 id="image-crop-title">Crop</h3>
            <p id="image-crop-hint" className="image-crop-hint">
              Pinch to zoom, drag to move, pull the corners to resize.
            </p>
          </div>
          {pixelCrop && status === 'ready' && (
            <p className="image-crop-size" aria-live="polite">
              {pixelCrop.width} × {pixelCrop.height}
            </p>
          )}
        </div>

        <div className="image-crop-toolbar" role="toolbar" aria-label="Crop tools">
          <button type="button" className="image-crop-tool" onClick={() => handleRotate(-90)} disabled={disabled} aria-label="Rotate left">
            <RotateLeftIcon />
          </button>
          <button type="button" className="image-crop-tool" onClick={() => handleRotate(90)} disabled={disabled} aria-label="Rotate right">
            <RotateRightIcon />
          </button>
          <button type="button" className="image-crop-tool" onClick={handleReset} disabled={disabled} aria-label="Reset crop">
            <ResetIcon />
          </button>
          <span className="image-crop-toolbar-spacer" />
          <button type="button" className="image-crop-tool" onClick={() => zoomBy(1 / ZOOM_STEP)} disabled={disabled} aria-label="Zoom out">
            <MinusIcon />
          </button>
          <button type="button" className="image-crop-tool" onClick={() => zoomBy(ZOOM_STEP)} disabled={disabled} aria-label="Zoom in">
            <PlusIcon />
          </button>
        </div>

        <div
          ref={stageRef}
          className={`image-crop-stage${status === 'ready' ? '' : ' image-crop-stage--pending'}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onLostPointerCapture={onPointerUp}
          onContextMenu={(event) => event.preventDefault()}
        >
          {status === 'loading' && (
            <div className="image-crop-status" role="status">Loading image…</div>
          )}
          {status === 'error' && (
            <div className="image-crop-status" role="alert">
              {errorMessage || 'We could not load that image. Try another photo.'}
            </div>
          )}
          <div className="image-crop-image-layer" style={imageStyle} aria-hidden="true">
            <img
              ref={imageRef}
              alt=""
              src={imageSrc}
              draggable={false}
              onLoad={(event) => handleImageReady(event.target)}
              onError={() => {
                setStatus('error')
                setErrorMessage('We could not load that image. Try another photo.')
              }}
            />
          </div>
          {layout && status === 'ready' && (
            <div
              className="image-crop-box"
              style={{
                left: `${layout.crop.x}px`,
                top: `${layout.crop.y}px`,
                width: `${layout.crop.width}px`,
                height: `${layout.crop.height}px`
              }}
            >
              <div className="image-crop-grid" />
              {CROP_HANDLES.map((handle) => (
                <div
                  key={handle}
                  className={`image-crop-handle image-crop-handle--${handle}`}
                  data-crop-handle={handle}
                  aria-hidden="true"
                />
              ))}
            </div>
          )}
        </div>

        {errorMessage && status !== 'error' && (
          <p className="image-crop-error" role="alert">{errorMessage}</p>
        )}

        <div className="image-crop-actions">
          <button
            type="button"
            className="crop-confirm-button"
            onClick={handleConfirm}
            disabled={disabled}
          >
            {busy === 'cropping' ? 'Cropping…' : 'Crop & Process'}
          </button>
          <div className="image-crop-secondary-actions">
            <button
              type="button"
              className="crop-skip-button"
              onClick={handleSkip}
              disabled={disabled}
            >
              {busy === 'skipping' ? 'Using full image…' : 'Skip'}
            </button>
            <button
              type="button"
              className="crop-cancel-button"
              onClick={onCancel}
              disabled={Boolean(busy)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageCrop
